import { Box, Stack } from "@mui/joy";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import Link from "../../components/common/Link";
import useGetUrls from "../../hooks/useGetUrls";
import GoogleLoginButton from "../../components/user/GoogleLoginButton";
import AppleLoginButton from "../../components/user/AppleLoginButton";
import KakaoLoginButton from "../../components/user/KakaoLoginButton";
import { motion, PanInfo, useAnimationControls } from "framer-motion";

import promotion1 from "../../static/image/promotion/login/1.png";
import promotion2 from "../../static/image/promotion/login/2.png";
import promotion3 from "../../static/image/promotion/login/3.png";
import promotion4 from "../../static/image/promotion/login/4.png";
import promotion5 from "../../static/image/promotion/login/5.png";
import promotion6 from "../../static/image/promotion/login/6.png";
import promotion7 from "../../static/image/promotion/login/7.png";
import promotion8 from "../../static/image/promotion/login/8.png";
import promotion9 from "../../static/image/promotion/login/9.png";
import promotion10 from "../../static/image/promotion/login/10.png";
import distancingLogo from "../../static/image/common/distancing_logo.png";
import dividerImg from "../../static/image/login/login_divider.svg";
import useSafeAreaColor from "../../hooks/useSafeAreaColor";
import { getPromotionBySlug } from "../../components/task/payment/planTypes";
import { useRecoilValue } from "recoil";
import { loginHintAtom } from "../../recoil/loginHintAtom";
import { getLoginButtonLayout } from "../../components/user/loginButtonLayout";
import CollapsibleLoginButtons from "../../components/user/CollapsibleLoginButtons";

const carouselItems = [
  { title: "매일 주어지는 활동지", image: promotion1 },
  { title: "이론 배우기", image: promotion2 },
  { title: "전담 코치의 리딩", image: promotion3 },
  { title: "패턴 발견하기", image: promotion4 },
  { title: "명상 훈련", image: promotion5 },
  { title: "생각 다루기", image: promotion6 },
  { title: "추적하며 관리하기", image: promotion7 },
  { title: "100일간의 여정", image: promotion8 },
  { title: "나 이해하기", image: promotion9 },
  { title: "가치 발견하기", image: promotion10 },
];

const ITEM_WIDTH = 226;
const ITEM_GAP = 12.5;
const MAX_VIEWPORT_WIDTH = ITEM_WIDTH * 1.8 + ITEM_GAP * 2;

// 파트너 전용 로그인 — 디자인은 LoginPromotion 과 동일, 단 메인 인증 버튼 사용 + 문구만 변경.
// 로그인 성공 후 useLogin 의 returnUrl(POST_LOGIN_RETURN_URL_KEY)로 /partner/:slug 복귀,
// PartnerTossButton 이 autopay 플래그로 토스창 자동 오픈.
export default function PartnerLogin() {
  const { slug } = useParams();
  const promotion = getPromotionBySlug(slug);

  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  // 파트너 로그인은 웹 전용 → 항상 접힘 대상 (Figma 8529-15562 1행)
  const loginHint = useRecoilValue(loginHintAtom);
  const loginLayout = getLoginButtonLayout("web", loginHint);
  const [currentIndex, setCurrentIndex] = useState(0);
  const controls = useAnimationControls();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const backgroundColor = theme.vars.palette.Background.Level2;

  useSafeAreaColor(backgroundColor);

  useEffect(() => {
    controls.start({
      x: -currentIndex * (ITEM_WIDTH + ITEM_GAP),
      transition: { type: "spring", stiffness: 300, damping: 30 },
    });
  }, [currentIndex, controls]);

  const handleDragStart = (_: any, info: PanInfo) => {
    if (Math.abs(info.offset.x) > Math.abs(info.offset.y)) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.overflowY = "hidden";
      }
    }
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.overflowY = "auto";
    }

    const threshold = 50;
    const velocityThreshold = 500;

    let nextIndex = currentIndex;
    if (
      (info.offset.x < -threshold || info.velocity.x < -velocityThreshold) &&
      currentIndex < carouselItems.length - 1
    ) {
      nextIndex = currentIndex + 1;
    } else if (
      (info.offset.x > threshold || info.velocity.x > velocityThreshold) &&
      currentIndex > 0
    ) {
      nextIndex = currentIndex - 1;
    }

    if (nextIndex !== currentIndex) {
      setCurrentIndex(nextIndex);
    } else {
      controls.start({
        x: -currentIndex * (ITEM_WIDTH + ITEM_GAP),
        transition: { type: "spring", stiffness: 300, damping: 30 },
      });
    }
  };

  const childrenType = (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      spacing={4}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        pt: 4,
      }}
    >
      <img
        src={distancingLogo}
        alt="distancing logo"
        draggable={false}
        style={{ width: "138px", height: "auto", flexShrink: 0 }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "320px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 4 },
          flexShrink: 0,
        }}
      >
        <Stack spacing={"12px"} sx={{ textAlign: "center", width: "100%" }}>
          <CustomTypography
            variant="H1"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 800,
              whiteSpace: "pre-wrap",
            }}
          >
            {"효과가 입증된 비약물\n우울·불안 인지치료"}
          </CustomTypography>
          <CustomTypography
            variant="Subtitle"
            sx={{
              color: theme.vars.palette.Content.Medium,
              fontWeight: 500,
              whiteSpace: "pre-wrap",
            }}
          >
            {"전담 코치가 이끄는 맞춤 프로그램으로\n새로운 삶을 맞이하세요."}
          </CustomTypography>
        </Stack>

        <Stack
          spacing={"6px"}
          sx={{
            width: "100%",
          }}
          alignSelf="center"
        >
          <CollapsibleLoginButtons
            layout={loginLayout}
            buttonByMethod={{
              kakao: <KakaoLoginButton />,
              apple: <AppleLoginButton />,
              google: <GoogleLoginButton />,
            }}
          />
        </Stack>
      </Box>

      <img
        src={dividerImg}
        alt={"empty"}
        style={{
          width: "100%",
          maxWidth: "320px",
        }}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          touchAction: "pan-y",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: `${MAX_VIEWPORT_WIDTH}px`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <motion.div
            drag="x"
            dragDirectionLock
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            dragMomentum={false}
            dragElastic={0.1}
            dragConstraints={{
              left: -(carouselItems.length - 1) * (ITEM_WIDTH + ITEM_GAP),
              right: 0,
            }}
            initial={{ x: 0 }}
            animate={controls}
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              cursor: "grab",
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: "pan-y",
            }}
            whileTap={{ cursor: "grabbing" }}
          >
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
            {carouselItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: `0 0 ${ITEM_WIDTH}px`,
                  marginRight: `${ITEM_GAP}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  boxSizing: "border-box",
                }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: index === currentIndex ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <CustomTypography
                    variant="Body"
                    sx={{
                      py: 0,
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    {item.title}
                  </CustomTypography>
                </motion.div>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                flex: `0 0 calc(50% - ${ITEM_WIDTH / 2}px)`,
                flexShrink: 0,
              }}
            />
          </motion.div>
        </Box>
      </Box>
    </Stack>
  );

  if (!slug || !promotion) return <Navigate to="/" replace />;

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        zIndex: 1000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "calc(var(--vh,1vh) * 100)",
        backgroundColor: backgroundColor,
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "none",
      }}
    >
      <Stack
        direction={"column"}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "660px",
          minHeight: "100%",
          pb: "20px",
          position: "relative",
        }}
      >
        {childrenType}

        <Stack
          direction={"row"}
          spacing={"20px"}
          justifyContent="center"
          sx={{
            mt: "auto",
            mb: "40px",
            pb: "10px",
            pt: 4,
            wordBreak: "keep-all",
            flexShrink: 0,
          }}
        >
          <Link
            opacity={100}
            bold={false}
            text={"사업자 정보"}
            url={companyInfoUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"개인정보 처리방침"}
            url={privacyPolicyUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"서비스 이용약관"}
            url={servicePolicyUrl}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
